import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { BellRing, Check } from "lucide-react";
import { AREA_GROUPS } from "../data";
import { Reveal } from "./Reveal";
import { trackLead } from "../lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const Input = "w-full bg-white border border-black/20 px-4 py-3 text-base focus:outline-none focus:ring-1 focus:ring-[#0a0a0a] focus:border-[#0a0a0a] transition-colors";

// fixedArea: when rendered on an area landing page, locks the area selector
export const AreaAlert = ({ fixedArea = null, dark = false }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [area, setArea] = useState(fixedArea || "");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return toast.error("Please enter your email");
    if (!area) return toast.error("Please choose an area");
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        source: "area_alert", name: name || null, email, area,
        message: `Wants new-listing alerts for ${area}`
      });
      trackLead({ source: "area_alert", area });
      setDone(true);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const wrap = dark ? "bg-white/5 border border-white/10" : "bg-white border border-black/5";
  const labelColor = dark ? "text-white/70" : "text-[#52525b]";

  return (
    <div data-testid="area-alert" className={`${wrap} p-8 sm:p-10`}>
      {done ? (
        <div className="flex flex-col items-center text-center py-6" data-testid="area-alert-success">
          <div className="w-14 h-14 bg-gold/10 flex items-center justify-center mb-5">
            <Check className="text-gold" size={26} />
          </div>
          <h4 className={`font-heading text-2xl ${dark ? "text-white" : ""}`}>You're on the list!</h4>
          <p className={`font-light mt-3 ${labelColor}`}>
            We'll email you the moment a new home hits the market in <span className="text-gold font-medium">{area}</span>.
          </p>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <div className="flex items-center gap-3">
            <BellRing className="text-gold" size={22} />
            <h4 className={`font-heading text-2xl ${dark ? "text-white" : ""}`}>
              {fixedArea ? `Get new ${fixedArea} listings first` : "Get new listings before anyone else"}
            </h4>
          </div>
          <p className={`font-light text-sm ${labelColor}`}>
            Be first to know when homes hit the market in your favorite area.
          </p>
          <input data-testid="area-alert-name" className={Input} placeholder="Name (optional)" value={name} onChange={(e) => setName(e.target.value)} />
          <input data-testid="area-alert-email" type="email" className={Input} placeholder="Email address" value={email} onChange={(e) => setEmail(e.target.value)} />
          {!fixedArea && (
            <select data-testid="area-alert-area" className={Input} value={area} onChange={(e) => setArea(e.target.value)}>
              <option value="">Which area are you watching?</option>
              {AREA_GROUPS.map((g) => (
                <optgroup key={g.label} label={g.label}>
                  {g.options.map((o) => <option key={o} value={o}>{o}</option>)}
                </optgroup>
              ))}
            </select>
          )}
          <button data-testid="area-alert-submit" type="submit" disabled={loading} className="w-full bg-gold text-white py-4 text-sm tracking-wide hover:bg-gold-hover transition-colors disabled:opacity-60">
            {loading ? "Subscribing..." : "Notify Me"}
          </button>
        </form>
      )}
    </div>
  );
};

export const AreaAlertSection = () => (
  <section id="alerts" data-testid="alerts-section" className="py-24 md:py-32 bg-surface">
    <div className="max-w-6xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
      <Reveal>
        <p className="eyebrow text-gold mb-4">Buyer Alerts</p>
        <h2 className="font-heading font-light text-4xl sm:text-5xl tracking-tight leading-tight">
          Homes move fast. Be there first.
        </h2>
        <p className="text-[#52525b] font-light text-lg mt-6 leading-relaxed">
          Tell us which area you're watching and we'll send you new listings the moment they go live —
          often before they ever reach the big portals.
        </p>
      </Reveal>
      <Reveal delay={0.15}>
        <AreaAlert />
      </Reveal>
    </div>
  </section>
);
